import get from 'lodash/get';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import { all, takeLatest, put, call, select } from 'redux-saga/effects';
import { updateCartItem } from 'yoda-interfaces/lib/Cart/CartApi';
import { fetchStores } from 'yoda-interfaces/lib/Common/LocationServiceApi';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import { messagesActions } from 'yoda-site-components/lib/components/Messages';
import loadingActions from 'yoda-site-components/lib/actions/LoadingAction';
import {
    setSdpuLocalStorage,
    getSdpuStoreData,
} from 'yoda-site-components/lib/actions/SdpuStoreInfoAction';
import { X_CLIENT_SOURCE_PDP } from 'yoda-site-components/lib/common/Constants';
import {
    selectFeatureFlags,
    selectBagItemCount,
    selectIsNative,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import productPricingSelector from 'yoda-site-components/lib/selectors/ProductPricingSelector';
import { getDisableBYDeliveryDateFlag } from 'yoda-site-components/lib/selectors/byDeliveryDateSelector';
import selectProductType from 'yoda-site-components/lib/selectors/productDetails/selectProductType';
import { updateCartCount } from 'yoda-site-components/lib/actions/NativeAppAction';
import {
    ADD_TO_CART_MESSAGES_ID,
    STORE_MODAL_ADD_TO_CART_MESSAGES_ID,
} from '../components/AddToCart/AddToCart.config';
import { ITEM_UPDATE_REQUEST, ITEM_UPDATE_SUCCESS } from '../actionTypes/AddToCartActionTypes';
import { formErrorAnalytics } from '../actions/AnalyticsAction';
import ClickStreamLogger from '../helpers/ClickStreamLogger';
import GetClickStreamLogData from '../helpers/PdpAtcClickStreamLogData';

const addCartClickPosition = (state) => state.addToCart.stickyHeaderOnClick;
const getGenericApiErrorMessages = (state) => state.context.messagesTexts.GenericApiErrorMessages;
const getDeliveryOptions = ({ productOffering }) =>
    get(productOffering.promise, 'skuDeliveryOptions[0].deliveryOptions', {});

function* cartItemUpdateSaga(action) {
    const { isStoreModalAtc = false } = action && action.payload;
    const ADD_TO_CART_MSG_ID = isStoreModalAtc
        ? STORE_MODAL_ADD_TO_CART_MESSAGES_ID
        : ADD_TO_CART_MESSAGES_ID;
    const requestParam = { ...action };

    const disableBYDeliveryDate = yield select(getDisableBYDeliveryDateFlag);
    requestParam.payload = {
        ...omit(action.payload, [
            'deliveryOption',
            'isChangeStoreAtc',
            'isStoreModalAtc',
            'sdpuStoreInfo',
            'location',
            'router',
        ]),
        xClientSource: !disableBYDeliveryDate ? X_CLIENT_SOURCE_PDP : null,
    };
    const result = yield call(FactorySaga, updateCartItem, requestParam);
    if (result.isSuccess) {
        yield put(loadingActions.removeLoader());
        const isNative = yield select(selectIsNative);
        if (isNative) {
            const { ppId, quantity, skuId } = requestParam?.payload?.params;
            const bagItemCount = yield select(selectBagItemCount);
            const pricingDetails = yield select(productPricingSelector(false));
            const price =
                !pricingDetails?.mapPrice && (pricingDetails?.defaults || pricingDetails?.original);
            const cartItemsArr = [
                {
                    ppId,
                    quantity,
                    price: price || '0.0',
                    skuId,
                },
            ];

            yield put(updateCartCount(bagItemCount, cartItemsArr));
        }
        const featureFlags = yield select(selectFeatureFlags);
        const productType = yield select(selectProductType()) || {};
        if (
            get(featureFlags, 'enableClickStreamLogs', true) &&
            productType.templateType !== 'bundle'
        ) {
            const deliveryOptionsOffering = yield select(getDeliveryOptions);
            const clickStreamLogData = GetClickStreamLogData(deliveryOptionsOffering, action);
            yield call(ClickStreamLogger, clickStreamLogData);
        }
        const sdpuStoreInfo = get(action, 'payload.sdpuStoreInfo', {});
        const deliveryOption = get(action, 'payload.deliveryOption', {});
        result.stickyHeaderOnClick = yield select(addCartClickPosition);
        yield all([
            put({
                type: ITEM_UPDATE_SUCCESS,
                result: {
                    ...result,
                    isStoreModalAtc,
                },
            }),
        ]);

        const isBopisChecked =
            isStoreModalAtc ||
            (deliveryOption.sameDayPickUp && deliveryOption.sameDayPickUp.isChecked);

        const isBopisATC = !!(isBopisChecked && sdpuStoreInfo.storeId && sdpuStoreInfo.storeName);
        const fetchStoreResponse = yield call(fetchStores, sdpuStoreInfo.storeId);
        const curbside = get(fetchStoreResponse, 'data.stores[0].curbside', null);
        sdpuStoreInfo.curbsidePickupIndicator = !isEmpty(curbside);
        if (isBopisATC) {
            yield all([put(getSdpuStoreData(sdpuStoreInfo)), put(setSdpuLocalStorage())]);
        }
        // To remove cartId from parameter to avoid showing update cart even after added item removed
        if (typeof URL !== 'undefined') {
            const updatedUrl = new URL(location.href);
            updatedUrl.searchParams.delete('cartId');
            history.replaceState(null, '', updatedUrl.href);
        }
    } else if (result.response && result.response.status !== 500) {
        // when API fails we get 500 response and we display generic error.
        const { errorCode, errorMessage } = Array.isArray(result.response.data)
            ? result.response.data[0]
            : result.response.data;
        yield put(loadingActions.removeLoader());
        yield put(
            messagesActions.addMessage({
                section: ADD_TO_CART_MSG_ID,
                message: {
                    id: errorCode,
                    title: errorMessage,
                },
                sourceMiniPDP: false,
            })
        );
        yield put(formErrorAnalytics([{ errorDescription: errorMessage.toLowerCase() }]));
    } else {
        const GenericApiErrorMessages = yield select(getGenericApiErrorMessages);
        const errorTitle = GenericApiErrorMessages.apiErrorMessage;
        yield put(loadingActions.removeLoader());
        yield put(
            messagesActions.addMessage({
                section: ADD_TO_CART_MSG_ID,
                message: {
                    id: 'SVC_GENERIC_ERROR',
                    title: errorTitle,
                },
                sourceMiniPDP: false,
            })
        );
        yield put(formErrorAnalytics([{ errorDescription: errorTitle.toLowerCase() }]));
    }
}

const watchUpdateCartSaga = function* watchUpdateCartSaga() {
    yield takeLatest(ITEM_UPDATE_REQUEST, cartItemUpdateSaga);
};

watchUpdateCartSaga.sagaName = 'updateCartSaga';
export default watchUpdateCartSaga;
